import { Component, effect, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, SortEvent } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { PaginatorState } from 'primeng/paginator';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues, ModalService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { LabDip } from '../../../models/lab-dip/lab-dip';
import { LabDipSearch } from '../../../models/commands/lab-dip-search';
import { LocaleHelperService } from '../../../services/localehelperservice.service';
import { LabDipProxyService } from '../../../proxies/item/lab-dip.proxy.service';
import { LabDipDetailsModalComponent } from '../../modals/lab-dip-details-modal/lab-dip-details-modal.component';
import { LapDipRoles } from '../../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-lab-dips',
  templateUrl: './lab-dips.component.html',
  styleUrl: './lab-dips.component.scss',
})
export class LabDipsComponent implements OnDestroy {
  @ViewChild('menu') menu!: Menu;

  private router = inject(Router);
  private translate = inject(TranslateService);
  private labDipProxyService = inject(LabDipProxyService);
  private modalService = inject(ModalService);
  public localeHelperService = inject(LocaleHelperService);

  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'lab-dips-filters', config: this.filterConfig });
  showFilters = signal<boolean>(true);
  menuItems = signal<MenuItem[]>([]);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(25);
  totalRecords = signal(0);

  // Sorting
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  // Data
  listLabDips = signal<LabDip[]>([]);

  private destroy$ = new Subject<void>();
  private lastSearchCriteria: LabDipSearch = {};

  getRowData(rowData: unknown): LabDip {
    return rowData as LabDip;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        if (this.translate.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translate.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translate.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'labDipNumber',
          label: translate('LabDips_Number', 'Lab Dip Number'),
          placeholder: translate('LabDips_Enter_Number', 'Enter lab dip number'),
          defaultVisible: true,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private onSearch(searchCriteria: LabDipSearch): void {
    this.lastSearchCriteria = searchCriteria;
    this.startLoading();
    this.labDipProxyService
      .search(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.listLabDips.set(response?.data?.items || []);
          this.totalRecords.set(response?.data?.totalCount || 0);
        },
        error: () => {
          this.listLabDips.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page || 0) + 1);
    this.pageSize.set(event.rows || this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order ?? -1);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const searchCriteria: LabDipSearch = {
      labDipNumber: (filters['labDipNumber'] as string)?.trim() || undefined,
      unassigned: (filters['unassigned'] as boolean | null | undefined) ?? undefined,
    };

    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  redirectToDetail(id?: string): void {
    const queryParams = id ? { id: id } : {};
    this.router.navigate(['/lab-dip-details'], { queryParams });
  }

  canEdit(): boolean {
    if (AuthService.hasRole(LapDipRoles.LabDipAdmin) || AuthService.hasRole(LapDipRoles.LabDipEditor)) {
      return true;
    }
    return false;
  }

  openLabDipDetailsModal(id: string): void {
    this.modalService
      .open(
        LabDipDetailsModalComponent,
        {},
        {
          id: id,
          canEdit: this.canEdit(),
        },
      )
      .subscribe((result) => {
        if (result) {
          this.onSearch({});
        }
      });
  }

  goToRecipeDetails(id?: string): void {
    this.router.navigate(['/recipe-details'], { queryParams: { id } });
  }

  goToRecipeContainer(labDipId?: string): void {
    if (labDipId) {
      this.router.navigate(['/recipe-container'], { queryParams: { labDipId } });
    }
  }

  toggleMenu(event: Event, labDip: LabDip): void {
    this.menuItems.set([
      {
        label: this.translate.instant('LabDips_ViewRecipes') || 'View Recipes',
        icon: 'pi pi-eye',
        command: () => {
          if (labDip.id) {
            const targetLabDipId = labDip.id;
            this.router.navigate(['/recipe-container'], {
              queryParams: { labDipId: targetLabDipId },
            });
          }
        },
      },
    ]);
    this.menu.toggle(event);
  }
}
